import {
  Delete,
  Preview,
  FileDownloadSharp,
  CloudUploadOutlined,
  InsertDriveFileOutlined,
} from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Box, IconButton, styled, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  avi,
  csv,
  doc,
  docx,
  flv,
  gif,
  html,
  jpeg,
  mp3,
  mp4,
  mpg,
  ogv,
  pdf,
  png,
  rtf,
  txt,
  webm,
  wma,
  xls,
  xlsx,
  zip,
  ts,
  mkv,
  mov,
  wmv,
  video3gp,
  video3g2,
  m4v,
  asf,
  f4v,
  rm,
  rmvb,
  mxf,
  vob,
  dv,
  amv,
  CAN_ACCESS_APPLICATION,
} from "../../constants/applicationConstants";

import { Controller, Control } from "react-hook-form";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { SingleAlertInfo, setSingleAlertObj } from "../../redux/slice/commonSlice";
import OptionsPopup from "./OptionsPopup";
import { fetchDocumentById } from "../../redux/slice/practiceSlice";
import AiMedicalAuditToolButton from "../operation/aiMedicalAudit/MedicalNoteAuditInlineButton";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { selectIsValueFoundInToken, useAuthSlice } from "../../redux/slice/authSlice";

interface FileSizeLimit {
  size: number;
  unit: 'KB' | 'MB' | 'GB';
}

export interface ExtendedFile extends File {
  documentId?: string;
}


interface DropzoneProps {
  name: string;
  onFileChange: (uploadedFiles: ExtendedFile[]) => any;
  label?: string;
  customDropzoneText?: string; // Text to display in the dropzone area
  subLabel?: {textColor : string, text : JSX.Element};
  allowedFileTypes?: { [mimeType: string]: string[] };
  maxFileSize?: number | FileSizeLimit; 
  maxFileCount?: number;
  maxFileAllowed?: number; // CS-1706 - New prop to enforce strict file limit at browser level
  onFileChangeTest?: (uploadedFiles: ExtendedFile[], name: string) => any;
  initialFiles?: ExtendedFile[];
  initialFilesMetaData?: ExtendedFile[];
  control?: Control<any, any>;
  rules?: {
      required: string;
    };
  isDisabled?: boolean;
  onDeleteFile?: (documentId: any) => any;
  holdOnChange?: boolean;
  showUploadedFiles?:boolean;
  showMedicalAuditButton?: boolean;
  patientId?: number | string | null;
  patientName?: string;  // CS-1773 - Run Medical Note Audit AI Tool in Background
  docType?: string;
  // CS-1594 - Audit data management props
  auditDataByDocument?: Map<string, any>;
  onAuditComplete?: (documentId: string, auditData: any) => void;
  // CS-XXXX - Callback to close all popups (upload popup + audit popup)
  onCloseAllPopups?: () => void;
  skipAuditStartedWaitContinuePopup?: boolean;
  // CS-1758 - Custom action renderer for each file row
  renderExtraFileAction?: (file: ExtendedFile) => React.ReactNode;
}
/**
 * allowedFileTypes - Used for custom file types. For single file type, allowedFileTypes = {png}. For multiple file types, allowedFileTypes = {{...png, ...jpeg}}.
 * 
 * maxFileSize - Used to set the maximum upload file size in bytes. If not provided, no limit will be applied. When limiting the file size, maxFileSize={{size: 1, unit: 'MB'}}.
 * 
 * maxFileCount - Used to set the maximum number of files can be uploaded. For a single file upload, maxFileCount = {1}.
 * 
 * CS-1706 START - maxFileAllowed - New prop to enforce strict file limit at browser level. When set to 1, prevents multi-select in file browser and shows error if file already exists.
 * 
 * */

/* ── Brand accent — matches useModalStyles()'s tokens.accent used app-wide ── */
const DZ_ACCENT = "#14b8a6";

const DropzoneArea = styled("div")(({ theme }) => {
  const isDark = theme.palette.mode === "dark";
  return {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: "6px",
    padding: "22px 16px",
    borderWidth: "1.5px",
    borderRadius: "12px",
    borderColor: isDark ? "rgba(255,255,255,0.16)" : "#cbd5e1",
    borderStyle: "dashed",
    backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
    color: isDark ? "#94a3b8" : "#64748b",
    transition: "border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease",
    "&:hover": {
      borderColor: DZ_ACCENT,
      backgroundColor: isDark ? "rgba(20,184,166,0.06)" : "rgba(20,184,166,0.05)",
      color: DZ_ACCENT,
    },
  };
});

const Dropzone: React.FC<DropzoneProps> = ({
  name,
  onFileChange,
  onFileChangeTest,
  label,
  customDropzoneText,
  subLabel,
  allowedFileTypes,
  maxFileSize,
  maxFileCount,
  maxFileAllowed,
  initialFiles,
  initialFilesMetaData,
  control,
  rules,
  isDisabled,
  onDeleteFile,
  holdOnChange = false,
  showUploadedFiles = false,
  showMedicalAuditButton = false,
  patientId = null,
  patientName,
  docType = "",
  // CS-1594 - Audit data management props
  auditDataByDocument,
  onAuditComplete,
  // CS-XXXX - Callback to close all popups
  onCloseAllPopups,
  skipAuditStartedWaitContinuePopup = false,
  // CS-1758 - Custom action renderer for each file row
  renderExtraFileAction,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isFileDropped, setIsFileDropped] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [fileRejections, setFileRejections] = useState<any[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<ExtendedFile[]>(
    initialFiles || initialFilesMetaData || []
  );
  const [confirmationMessage, setConfirmationMessage] = useState<string>("");
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [confirmedAction, setConfirmedAction] = useState<(() => void) | null>(null);
  const DEFAULT_MAX_SIZE = Infinity;
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [fileUrl, setFileUrl] = useState<string>("");
  const [fetchedFile, setFetchedFile] = useState<ExtendedFile>();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // CS-1426

  // CS-1426 - Check if user has access to Medical Note Audit feature
  const hasSubscribedAccessToMedicalAudit = useSelector((state: RootState) => 
    selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.MEDICAL_NOTE_AUDIT)(state)
  );
       const { userInfo } = useAuthSlice();

  function convertFileSizeToBytes(fileSizeLimit?: number | FileSizeLimit): number {
    if (typeof fileSizeLimit === 'number') {
      return fileSizeLimit;
    } else if (fileSizeLimit) {
      const { size, unit } = fileSizeLimit;
      const unitConversions = {
        KB: 1024,
        MB: 1024 * 1024,
        GB: 1024 * 1024 * 1024,
      };
      return size * unitConversions[unit];
    } else {
      return DEFAULT_MAX_SIZE;
    }
  };

  useEffect(() => {
    if (initialFiles) {
      setUploadedFiles(initialFiles);
    } 
    else if (initialFilesMetaData) {
      setUploadedFiles(initialFilesMetaData);
    }
  }, [initialFiles, initialFilesMetaData]);

  const updateFilesInState = (newFiles: ExtendedFile[]) => {
    setUploadedFiles(newFiles);
  };

  const isFileSizeValid = (file: ExtendedFile) => {
    if (!maxFileSize) return true;
    const maxFileSizeInBytes = convertFileSizeToBytes(maxFileSize);
    return file.size <= maxFileSizeInBytes;
  };

  const onDragEnter = () => {
    setIsFileDropped(false);
  };

  const onDragOver = () => {
    setIsFileDropped(false);
  };

  const onDragLeave = () => {
    setIsFileDropped(false);
  };

  const resetConfirmationState = () => {
    setIsConfirmationOpen(false);
    setConfirmationMessage("");
    setConfirmedAction(null);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    
  };
  

  const handleRenameUpload = (file: ExtendedFile, fileName: string, fileExtension: string) => {
    let newFile = file;
    let counter = 1;
    while (uploadedFiles.some((uploadedFile) => uploadedFile.name === newFile.name)) {
      newFile = new File([file], `${fileName}(${counter})${fileExtension}`, {
        type: file.type,
      }) as ExtendedFile;
      counter++;
    }
    
    setUploadedFiles((prevFiles) => {
      let newFiles = [...prevFiles, newFile];
      onFileChange(newFiles);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onFileChangeTest && onFileChangeTest(newFiles, name);
      return newFiles;
    });
  
    setIsConfirmationOpen(false);
  };

  // CS-1706 START - Validate maxFileAllowed limit before processing files
  const onDrop = async (acceptedFiles: ExtendedFile[], fileRejections: any) => {
    try {
      // console.log(fileRejections); // CS-1984
      setIsDragActive(false);
      setIsFileDropped(true);
  
      const validFiles = acceptedFiles.filter(isFileSizeValid);
      
      // If maxFileAllowed is set and files already exist, show error and return
      if (maxFileAllowed && uploadedFiles.length >= maxFileAllowed) {
        dispatch(setSingleAlertObj({
          message: maxFileAllowed === 1 
            ? "Only one file is allowed. Please delete the existing file before uploading a new one."
            : `Maximum ${maxFileAllowed} files allowed. Please delete existing files before uploading new ones.`,
          alertType: "error",
        }));
        return;
      }
      // CS-1706 END
      
      const spaceAvailable = maxFileCount
        ? maxFileCount - uploadedFiles.length
        : validFiles.length;
      const filesToAdd = validFiles.slice(0, spaceAvailable);
      
      const renamedFiles: any = [];

      for (let file of filesToAdd) {
        let newFile = file;
        let fileName = file.name;
        let fileExtension = '';
        const dotIndex = fileName.lastIndexOf('.');
        
        if (dotIndex !== -1) {
          fileExtension = fileName.slice(dotIndex);
          fileName = fileName.slice(0, dotIndex);
        }
    
        while (uploadedFiles.some((uploadedFile) => uploadedFile.name === newFile.name)) {
          setConfirmationMessage(`File "${newFile.name}" already exists. Do you want to rename and upload?`);
          setConfirmedAction(() => () => {
            handleRenameUpload(file, fileName, fileExtension);
          });
          setIsConfirmationOpen(true);
          return;
        }
        renamedFiles.push(newFile);
      }
  
      if (renamedFiles.length > 0) {
        setUploadedFiles((prevFiles) => {
          let newFiles = [...prevFiles, ...renamedFiles];
          onFileChange(newFiles);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          onFileChangeTest && onFileChangeTest(newFiles, name);
          return newFiles;
        });
      }
  
      if (fileRejections.length > 0) {
        const emptyFileRejections = fileRejections.filter((r: any) =>
          r.errors?.some((e: any) => e.code === 'file-too-small')
        );
        const emptyFileCount = emptyFileRejections.length;

        if (emptyFileCount > 0) {
          dispatch(setSingleAlertObj({
            message: emptyFileCount === 1
              ? "Upload failed: Empty files (0 KB) are not allowed. Please select a valid file."
              : `${emptyFileCount} files were not added. Empty files (0 KB) are not allowed. Please select a valid file.`,
            alertType: "error",
          }));
        }

        fileRejections.forEach((rejection: any) => {
          if (rejection.errors?.some((e: any) => e.code === 'file-too-small')) {
            return;
          }
          const file = rejection.file;
          const fileName = file.name;
          const fileExtension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
          let errorObj: SingleAlertInfo = {
              message: '',
              alertType: 'error'
          };
          if (rejection.errors.some((e: any) => e.code === 'file-too-large')) {
            errorObj.message = `Invalid file size. Maximum size allowed is ${
              convertFileSizeToBytes(maxFileSize) / (1024 * 1024)
            } MB.`;
          } else if (rejection.errors.some((e: any) => e.code === 'file-invalid-type')) {
            if (allowedFileTypes) {
                errorObj.message = `Invalid file type "${fileExtension}". Allowed types are ${Object.keys(
                  allowedFileTypes
                ).map(key => allowedFileTypes[key].map(ext => ext)).flat().join(", ")}.`;
            } else {
                errorObj.message = `Invalid file type "${fileExtension}".`;
            }
          } else if (rejection.errors.some((e: any) => e.code === 'too-many-files')) {
            // CS-1706 START - Handle too-many-files error with maxFileAllowed
            errorObj.message = maxFileAllowed 
              ? (maxFileAllowed === 1 
                  ? "Only one file is allowed. Please delete the existing file before uploading a new one."
                  : `Maximum ${maxFileAllowed} files allowed.`)
              : "Too many files selected.";
            // CS-1706 END
          } else {
            errorObj.message = "File could not be uploaded due to restrictions.";
          }
          dispatch(setSingleAlertObj(errorObj));
        });
      }
    } catch (error) {
      console.error("Error handling the file drop:", error);
      setIsDragActive(false);
      setIsFileDropped(false);
    }
  };
  
  const deleteFile = (index: number, docId: any) => {
    const uploadedFileElement = document.querySelector(`.uploaded-file[data-document-id="${docId}"]`);
    const documentId = uploadedFileElement?.getAttribute("data-document-id");
    onDeleteFile && onDeleteFile(documentId);
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    if (onFileChangeTest) onFileChangeTest(newFiles, name);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if(!holdOnChange){
      onFileChange(newFiles);
    }
  };

  const maxSizeInBytes = useMemo(() => {
    return typeof maxFileSize === 'number' ? maxFileSize : convertFileSizeToBytes(maxFileSize);
  }, [maxFileSize]);

  // CS-1706 START - Configure useDropzone with maxFileAllowed to enforce single file selection at browser level
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) =>
      onDrop(acceptedFiles, fileRejections),
    accept: allowedFileTypes || {
      ...pdf,
      ...doc,
      ...docx,
      ...xls,
      ...xlsx,
      ...zip,
      ...csv,
      ...txt,
      ...rtf,
      ...html,
      ...mp3,
      ...wma,
      ...mpg,
      ...flv,
      ...avi,
      ...mp4,
      ...ogv,
      ...ts,
      ...webm,
      ...mkv,
      ...mov,
      ...wmv,
      ...video3gp,
      ...video3g2,
      ...m4v,
      ...asf,
      ...f4v,
      ...rm,
      ...rmvb,
      ...mxf,
      ...vob,
      ...dv,
      ...amv,
      ...jpeg,
      ...png,
      ...gif,
    },
    maxSize: maxSizeInBytes,
    minSize: 1,
    maxFiles: maxFileAllowed,
    multiple: maxFileAllowed !== 1,
    onDragEnter,
    onDragOver,
    onDragLeave,
    noClick: true,
  });
  // CS-1706 END

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const convertDocumentContentToExtendedFile = (
    documentData: any,
    doc: string
  ): ExtendedFile => {
    const byteCharacters = atob(documentData.content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: documentData.fileType });

    const extendedFile: ExtendedFile = new File(
      [blob],
      documentData.documentName,
      {
        type: documentData.fileType,
      }
    );
    extendedFile.documentId = doc;
    return extendedFile;
  };
  

  const fetchAndProcessDocument = async (
    documentId: string
  ): Promise<ExtendedFile | undefined> => {
    try {
      const actionResult = await dispatch(fetchDocumentById(documentId));
  
      if (actionResult?.payload) {
        const documentData = actionResult.payload;
        if (documentData.content) {
          const extendedFile = convertDocumentContentToExtendedFile(
            documentData,
            documentId
          );
  
          setFetchedFile(extendedFile); 
          return extendedFile;
        }
      }
  
      return undefined;
    } catch (error) {
      console.error(`Failed to fetch document with ID ${documentId}`, error);
  
      return undefined;
    }
  };

  const fetchFileUrl = async (documentId: string) => {
    try {
      const fileFetched = await fetchAndProcessDocument(documentId);
      if (fileFetched) {
        const newFileUrl = URL.createObjectURL(fileFetched);
        setFileUrl(newFileUrl);
        return newFileUrl;  
      } else {
        console.error("File could not be fetched");
        return '';
      }
    } catch (error) {
      console.error("Error during file fetch:", error);
      return '';
    }
  };

  const handleViewFileClick = async (e: React.MouseEvent, file: ExtendedFile) => {
    e.preventDefault();
    try {
      const url = await fetchFileUrl(file.documentId ? file.documentId : "");
      if (url) {
        setFileUrl(url);  
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Error fetching file URL:", error);
    }
  };

  // CS-1595 - Handler to fetch file for AI audit
  const handleFetchFileForAudit = async (file: ExtendedFile): Promise<ExtendedFile | undefined> => {
    try {
      // If file has documentId, it's from initialFilesMetaData - fetch it
      if (file.documentId) {
        const fetchedFile = await fetchAndProcessDocument(file.documentId);
        if (fetchedFile) {
          return fetchedFile;
        } else {
          const alertObj: SingleAlertInfo = {
            message: "Failed to fetch document for audit. Please try again.",
            alertType: "error",
          };
          dispatch(setSingleAlertObj(alertObj));
          return undefined;
        }
      } else {
        // File is already in memory (newly uploaded)
        return file;
      }
    } catch (error) {
      const alertObj: SingleAlertInfo = {
        message: "Error fetching file for audit. Please try again.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
      return undefined;
    }
  };
  
  const downloadFile = async (file: ExtendedFile, fileUrl: string) => {
    try {
      if (!fileUrl || fileUrl==='' || initialFilesMetaData) {
        fileUrl = await fetchFileUrl(file.documentId ? file.documentId : "");
      }
      if (fileUrl) {
        const downloadLink = document.createElement("a");
        downloadLink.href = fileUrl;
        downloadLink.download = file.name;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        URL.revokeObjectURL(fileUrl);
      } else {
        console.error("File could not be fetched.");
      }
    } catch (error) {
      console.error("Error during file download:", error);
    }
  };

  // CS-1426 - Start
  const renderUploadedFiles = () => {

    if (initialFilesMetaData) {
      // Render already uploaded files (with documentId - need to fetch before audit)
      return uploadedFiles.map((file, index) => {

        const maxNameLength = isMobile ? 15 : 27;
        const trimmedFileName =
          file.name?.length > maxNameLength
            ? file.name.substring(0, maxNameLength) + "..."
            : file.name;

        const fileSizeDisplay =
          file.size < 1024 * 1024
            ? `${(file.size / 1024).toFixed(2)} KB`
            : `${(file.size / 1024 / 1024).toFixed(2)} MB`;

        const isPdfFile = file.type === 'application/pdf';

        const isDark = theme.palette.mode === "dark";
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1,
              gap: 1.1,
              mt:1,
              borderRadius: 3,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
              backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
            }}
          >
            <Box
              data-document-id={file.documentId}
              className="uploaded-file"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexGrow: 1,
                minWidth: 0,
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }} title={`${file.name} - ${fileSizeDisplay}`}>
                <Typography sx={{
                  fontSize: "0.88rem",
                  fontWeight: 600,
                  color: isDark ? "#e2e8f0" : "#1e293b",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {trimmedFileName}
                </Typography>
                <Typography sx={{ fontSize: "0.76rem", color: isDark ? "#64748b" : "#94a3b8" }}>
                  {fileSizeDisplay}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 0.4,
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <IconButton
                  size="small"
                  className="dz-download-file"
                  onClick={() => downloadFile(file, '')}
                  sx={{
                    color: isDark ? "#94a3b8" : "#64748b",
                    "&:hover": { color: "#14b8a6", backgroundColor: isDark ? "rgba(20,184,166,0.14)" : "rgba(20,184,166,0.08)" },
                  }}
                >
                  <FileDownloadSharp fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  component="a"
                  href={fileUrl || ''}
                  onClick={(e) => handleViewFileClick(e, file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: isDark ? "#94a3b8" : "#64748b",
                    "&:hover": { color: "#14b8a6", backgroundColor: isDark ? "rgba(20,184,166,0.14)" : "rgba(20,184,166,0.08)" },
                  }}
                >
                  <Preview className="dz-view-file" fontSize="small" />
                </IconButton>
                {!isDisabled ?
                  <IconButton
                    size="small"
                    className="dz-delete-file"
                    onClick={(e) => {
                      e.preventDefault();
                      deleteFile(index, file.documentId);
                    }}
                    sx={{
                      color: isDark ? "#94a3b8" : "#64748b",
                      "&:hover": { color: "#ef4444", backgroundColor: isDark ? "rgba(239,68,68,0.14)" : "rgba(239,68,68,0.08)" },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton> : null
                }
              </Box>
            </Box>

            {!isDisabled && showMedicalAuditButton && hasSubscribedAccessToMedicalAudit && isPdfFile && userInfo && userInfo?.subscribedServices && userInfo.subscribedServices.length > 0 && (
              <AiMedicalAuditToolButton
                uploadedPdfFile={null}
                size="small"
                onFetchFile={() => handleFetchFileForAudit(file)}
                patientId={patientId}
                patientName={patientName}
                docType={docType}
                documentId={file.documentId ? Number(file.documentId) : null}
                // CS-1594 - Pass existing audit data if available
                initialAuditData={auditDataByDocument?.get(file.documentId || '')}
                auditCompleted={!!auditDataByDocument?.get(file.documentId || '')}
                onAuditComplete={onAuditComplete}
                // CS-XXXX - Pass callback to close all popups
                onCloseAllPopups={onCloseAllPopups}
                skipAuditStartedWaitContinuePopup={skipAuditStartedWaitContinuePopup}
              />
            )}
            {/* CS-1758 - Render extra file action if provided */}
            {renderExtraFileAction && renderExtraFileAction(file)}
          </Box>
        );
      });
    } else {
      // Render newly uploaded files (already in memory - no fetch needed)
      return uploadedFiles.map((file, index) => {

        const maxNameLength = isMobile ? 15 : 27;
        const trimmedFileName =
          file.name?.length > maxNameLength
            ? file.name.substring(0, maxNameLength) + "..."
            : file.name;

        const fileSizeDisplay =
          file.size < 1024 * 1024
            ? `${(file.size / 1024).toFixed(2)} KB`
            : `${(file.size / 1024 / 1024).toFixed(2)} MB`;

        const fileUrl = URL.createObjectURL(file);
        const isPdfFile = file.type === 'application/pdf';

        const isDark = theme.palette.mode === "dark";
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1,
              gap: 1.2,
              px: 1.4,
              py: 1,
              borderRadius: "12px",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
              backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
            }}
          >
            <Box sx={{
              width: 38,
              height: 38,
              flexShrink: 0,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isDark ? "rgba(20,184,166,0.14)" : "rgba(20,184,166,0.10)",
              color: "#14b8a6",
            }}>
              <InsertDriveFileOutlined sx={{ fontSize: "1.3rem" }} />
            </Box>

            <Box
              data-document-id={file.documentId}
              className="uploaded-file"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexGrow: 1,
                minWidth: 0,
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }} title={`${file.name} - ${fileSizeDisplay}`}>
                <Typography sx={{
                  fontSize: "0.88rem",
                  fontWeight: 600,
                  color: isDark ? "#e2e8f0" : "#1e293b",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {trimmedFileName}
                </Typography>
                <Typography sx={{ fontSize: "0.76rem", color: isDark ? "#64748b" : "#94a3b8" }}>
                  {fileSizeDisplay}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 0.4,
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <IconButton
                  size="small"
                  className="dz-download-file"
                  onClick={() => downloadFile(file, fileUrl)}
                  sx={{
                    color: isDark ? "#94a3b8" : "#64748b",
                    "&:hover": { color: "#14b8a6", backgroundColor: isDark ? "rgba(20,184,166,0.14)" : "rgba(20,184,166,0.08)" },
                  }}
                >
                  <FileDownloadSharp fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  component="a"
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: isDark ? "#94a3b8" : "#64748b",
                    "&:hover": { color: "#14b8a6", backgroundColor: isDark ? "rgba(20,184,166,0.14)" : "rgba(20,184,166,0.08)" },
                  }}
                >
                  <Preview className="dz-view-file" fontSize="small" />
                </IconButton>
                {!isDisabled ?
                  <IconButton
                    size="small"
                    className="dz-delete-file"
                    onClick={(e) => {
                      e.preventDefault();
                      URL.revokeObjectURL(fileUrl);
                      deleteFile(index, file.documentId);
                    }}
                    sx={{
                      color: isDark ? "#94a3b8" : "#64748b",
                      "&:hover": { color: "#ef4444", backgroundColor: isDark ? "rgba(239,68,68,0.14)" : "rgba(239,68,68,0.08)" },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton> : null
                }
              </Box>
            </Box>

            {!isDisabled && showMedicalAuditButton && hasSubscribedAccessToMedicalAudit && isPdfFile && (
              <AiMedicalAuditToolButton 
                uploadedPdfFile={file}
                size="small"
                onFetchFile={() => handleFetchFileForAudit(file)}
                patientId={patientId}
                patientName={patientName}
                docType={docType}
                documentId={file.documentId ? Number(file.documentId) : null}
                // CS-1594 - Pass existing audit data if available
                initialAuditData={auditDataByDocument?.get(file.documentId || '')}
                auditCompleted={!!auditDataByDocument?.get(file.documentId || '')}
                onAuditComplete={onAuditComplete}
                // CS-XXXX - Pass callback to close all popups
                onCloseAllPopups={onCloseAllPopups}
                skipAuditStartedWaitContinuePopup={skipAuditStartedWaitContinuePopup}
              />
            )}
          </Box>
        );
      });
    }
  };
  // CS-1426 - End

  useEffect(() => {}, [isDragActive, fileRejections]);
  useEffect(() => {
      if (showUploadedFiles) {
        uploadedFiles.map((file, index)=>{
          deleteFile(index, file.documentId);
        });
      }
    }, [showUploadedFiles]);

  return (
    <>
    <div>
      {label && <div style={{ textAlign: "left",marginBottom : "5px" }}>{label} <span style={{color : subLabel?.textColor}}>{subLabel?.text}</span></div>}
      <Controller
        name={name}
        control={control}
        rules={rules ? rules : {}}
        defaultValue={initialFiles || initialFilesMetaData || ""}
        render={({ field, fieldState: { error } }) => (
          <>
      <DropzoneArea
        {...getRootProps()}
        className={isFileDropped ? "file-dropped" : ""}
        style={{
          borderColor: error ? "#ef4444" : isDisabled ? (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "#e2e8f0") : undefined,
        }}
        onClick={openFileDialog}
        sx={{ cursor: isDisabled ? "not-allowed" : "pointer", opacity: isDisabled ? 0.6 : 1 }}
      >
        <input
          {...getInputProps()}
          ref={fileInputRef}
          style={{ display: "none" }}
          name={name}
          disabled={isDisabled}
        />
        <CloudUploadOutlined
          sx={{ fontSize: 26 }}
          className="upload-icon"
        />
        <Typography
          id="dropzone-text2"
          sx={{ color: "inherit", fontWeight: 700, fontSize: "0.86rem" }}
        >
          {customDropzoneText ? customDropzoneText : "Click to Upload File or Capture Image"}
        </Typography>
      </DropzoneArea>
          {error && (
            <p style={{ color: '#ef4444', fontSize: '12px', marginBottom: '-3px'  }}>
              {rules ? rules.required : ""}
            </p>
          )}

      <div>{uploadedFiles.length > 0 && renderUploadedFiles()}</div>
      </>
        )}
      />
    </div>
    <OptionsPopup
    open={isConfirmationOpen}
    onClose={() => resetConfirmationState()}
    variant="confirm"
    message={confirmationMessage}
    buttons={[
      { name: "Confirm", color: 'primary', onClick: () => confirmedAction && confirmedAction() },
      { name: "Cancel", color: 'secondary', onClick: () => resetConfirmationState() }
    ]}
  />
  </>
  );
};

export default Dropzone;
